import React from "react";
import { Star, Quote } from "lucide-react";
import { useCarousel } from "../../hooks/useCarousel";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import placeholderImage from "../../assets/images/placeholder.jpg";

const testimonials = [
  {
    id: 1,
    name: "Venkatesh Reddy",
    location: "Nallagandla, Hyderabad",
    project: "Aparna Zicon - 3BHK",
    rating: 5,
    image: placeholderImage,
    quote:
      "We moved into Aparna Zicon last month and Iravatham did our kitchen and wardrobes. Actually, my wife was very particular about the kitchen design. The team listened to all her requirements and delivered in 20 days. The quality is really good - even after 6 months, everything looks new. No complaints at all.",
  },
  {
    id: 2,
    name: "Padmavathi Rao",
    location: "Kokapet, Hyderabad",
    project: "Veu - 2BHK Rental",
    rating: 5,
    image: placeholderImage,
    quote:
      "I have a rental property in Veu and needed something that looks good but also lasts. Iravatham's rental package was perfect - 3.2 lakhs for complete interiors. My tenants are very happy and I don't have to worry about repairs. The wardrobes are spacious and the kitchen is functional. Good value for money.",
  },
  {
    id: 3,
    name: "Srinivas Goud",
    location: "Tellapur, Hyderabad",
    project: "Brigade - 3BHK",
    rating: 5,
    image: placeholderImage,
    quote:
      "We bought a flat in Brigade Tellapur and were confused about which interior company to choose. My colleague recommended Iravatham. The best part was they explained everything - why they use BWP Ply for kitchen, MR Ply for wardrobes. The installation was clean, no mess. My wife is very happy with the design. Would definitely recommend them.",
  },
  {
    id: 4,
    name: "Lakshmi Devi",
    location: "Nallagandla, Hyderabad",
    project: "Aparna Zicon - 2BHK",
    rating: 5,
    image: placeholderImage,
    quote:
      "I was worried about the timeline because we had to move in quickly. But Iravatham completed everything in 18 days - kitchen, wardrobes, even the pooja room. The designer understood what we wanted and the 3D visualization helped a lot. The false ceiling work is also very nice. Overall, very satisfied with their service.",
  },
  {
    id: 5,
    name: "Ramesh Kumar",
    location: "Kokapet, Hyderabad",
    project: "Veu - 4BHK",
    rating: 5,
    image: placeholderImage,
    quote:
      "We did full home interiors in our Veu flat - kitchen, all wardrobes, living room unit, dining, everything. The team was professional throughout. They visited our site multiple times to get measurements right. The quality of materials is good - we can see the difference compared to our previous home. The doors they made are also very nice. Worth the investment.",
  },
  {
    id: 6,
    name: "Sujatha Reddy",
    location: "Tellapur, Hyderabad",
    project: "Brigade - 3BHK + Study",
    rating: 5,
    image: placeholderImage,
    quote:
      "My son needed a study room and we wanted it to be functional. Iravatham designed a nice study with proper storage for books and a good desk. The lighting they suggested is perfect for reading. The whole house looks modern now. The team was patient with all our questions and changes. Good experience overall.",
  },
];

export default function Testimonials() {
  const {
    emblaRef,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    selectedIndex,
    scrollTo,
  } = useCarousel({ loop: true, align: "center" });

  return (
    <section className="section-spacing bg-background-card">
      <div className="container-custom section-padding">
        <SectionHeading
          label="Client Stories"
          title="What our clients say after the handover."
          align="center"
        />

        <div className="relative">
          {/* Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="flex-none w-full md:w-[80%] lg:w-[60%] px-4"
                >
                  <AnimatedSection>
                    <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-soft">
                      <div className="flex items-start gap-4 mb-6">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-16 h-16 object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="font-heading text-lg text-brand-primary">
                            {testimonial.name}
                          </h4>
                          <p className="text-text-secondary text-sm">
                            {testimonial.location}
                          </p>
                          <p className="text-brand-accent text-sm font-medium">
                            {testimonial.project}
                          </p>
                          {/* Rating stars - visible on mobile, below project */}
                          <div className="flex gap-0.5 mt-2 md:hidden w-full">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star
                                key={i}
                                className="w-5 h-5 text-yellow-400 fill-current"
                              />
                            ))}
                          </div>
                        </div>
                        {/* Rating stars - visible on desktop, on the right */}
                        <div className="hidden md:flex gap-0.5">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-5 h-5 text-yellow-400 fill-current"
                            />
                          ))}
                        </div>
                      </div>

                      <div className="relative">
                        <Quote className="absolute -top-2 -left-2 w-10 h-10 text-brand-accent/10" />
                        <p className="text-text-primary text-lg leading-relaxed pl-6">
                          "{testimonial.quote}"
                        </p>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="w-12 h-12 rounded-full border-2 border-border-light flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all disabled:opacity-30"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    selectedIndex === index
                      ? "bg-brand-accent w-8"
                      : "bg-border-medium hover:bg-brand-accent/50"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="w-12 h-12 rounded-full border-2 border-border-light flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all disabled:opacity-30"
              aria-label="Next testimonial"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
